import React, { useState, useEffect, useRef } from 'react';
import {
  Sparkles,
  X,
  Send,
  Activity,
  MessageSquare,
  ShieldAlert,
  BookOpen,
  Copy,
  Check,
  RefreshCw,
  Server,
  Database,
  Terminal,
  Cpu,
  Layers,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { useAskAi } from '../../hooks/useAskAi';

const SUGGESTED_PROMPTS = [
  'How do I approve a warehouse loading request?',
  'What is the session inactivity timeout for users?',
  'What schema must Liquibase migrations use?',
  'Why do we separate command and query DTOs?'
];

const SIMULATED_ERRORS = [
  {
    title: 'HikariCP Pool Saturation',
    trace: 'org.springframework.jdbc.CannotGetJdbcConnectionException: HikariPool-tenant - Connection is not available, request timed out after 30000ms'
  },
  {
    title: 'Liquibase Missing Schema',
    trace: 'org.postgresql.util.PSQLException: ERROR: relation "yaseero_tenant.loading_requests" does not exist'
  },
  {
    title: 'Keycloak Issuer Mismatch',
    trace: 'JwtVerificationException: Invalid token issuer: expected https://auth.yaseero.ae/realms/yaseero-platform'
  }
];

const AskAiDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' | 'telemetry' | 'diagnose'
  const [inputText, setInputText] = useState('');
  const [customTrace, setCustomTrace] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const {
    messages,
    isLoading,
    sendMessage,
    clearMessages,
    healthStatus,
    fetchHealth,
    isHealthLoading,
    telemetryData,
    fetchTelemetry,
    isTelemetryLoading,
    isSuperAdmin
  } = useAskAi();

  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      fetchHealth();
      if (activeTab === 'telemetry' && isSuperAdmin) {
        fetchTelemetry();
      }
    }
  }, [isOpen, messages, activeTab, isSuperAdmin, fetchHealth, fetchTelemetry]);

  // Fallback to chat tab if current active tab is restricted
  useEffect(() => {
    if (!isSuperAdmin && (activeTab === 'telemetry' || activeTab === 'diagnose')) {
      setActiveTab('chat');
    }
  }, [isSuperAdmin, activeTab]);

  const handleSend = (textToSend = null, options = {}) => {
    const text = textToSend || inputText;
    if (!text || !text.trim()) return;
    sendMessage(text, options);
    if (!textToSend) setInputText('');
    if (activeTab !== 'chat') setActiveTab('chat');
  };

  const handleCopyText = (id, text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <>
      {/* ── Floating Launcher Button ────────────────────────────────────────── */}
      {!isOpen && (
        <button
          id="ask-ai-floating-trigger"
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2.5 px-4 py-3 rounded-full shadow-2xl bg-gradient-to-r from-sky-500 via-blue-600 to-indigo-600 hover:from-sky-600 hover:to-indigo-700 text-white font-medium text-sm transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-sky-500/25 border border-sky-400/30"
          title="Open Yaseero SRE AI Assistant"
        >
          <div className="relative flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-amber-200 animate-pulse" />
            <span className="absolute -top-1 -right-1 flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
            </span>
          </div>
          <span className="tracking-wide">Ask AI</span>
          <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full font-mono">
            SRE 24/7
          </span>
        </button>
      )}

      {/* ── Slide-Over Backdrop ────────────────────────────────────────────── */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/40 backdrop-blur-sm transition-opacity duration-300"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* ── Slide-Over Panel Container ──────────────────────────────────────── */}
      <div
        className={`fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[480px] bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* ── Header ────────────────────────────────────────────────────────── */}
        <div className="p-4 border-b border-slate-200 dark:border-slate-800 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center shadow-lg shadow-sky-500/30 text-white">
              <Sparkles className="w-5 h-5 text-amber-200" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-sm tracking-wide text-white">Ask AI Assistant</h3>
                <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-1.5 py-0.5 rounded-full font-mono">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                  Gemini Brain
                </span>
              </div>
              <p className="text-xs text-slate-400">24/7 SRE Triage & Domain Knowledge Base</p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={clearMessages}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              title="Clear conversation"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              title="Close drawer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ── Tab Switcher ──────────────────────────────────────────────────── */}
        <div className="flex border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 text-xs font-medium">
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex-1 py-2.5 px-3 flex items-center justify-center gap-1.5 border-b-2 transition-all ${
              activeTab === 'chat'
                ? 'border-blue-600 text-blue-600 dark:text-sky-400 dark:border-sky-400 bg-white dark:bg-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            <span>Assistant</span>
          </button>

          {isSuperAdmin && (
            <>
              <button
                onClick={() => {
                  setActiveTab('telemetry');
                  fetchTelemetry();
                }}
                className={`flex-1 py-2.5 px-3 flex items-center justify-center gap-1.5 border-b-2 transition-all ${
                  activeTab === 'telemetry'
                    ? 'border-blue-600 text-blue-600 dark:text-sky-400 dark:border-sky-400 bg-white dark:bg-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                }`}
              >
                <Activity className="w-3.5 h-3.5" />
                <span>Live Telemetry</span>
                <span className="text-[9px] bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 px-1 rounded font-mono font-bold">Admin</span>
              </button>

              <button
                onClick={() => setActiveTab('diagnose')}
                className={`flex-1 py-2.5 px-3 flex items-center justify-center gap-1.5 border-b-2 transition-all ${
                  activeTab === 'diagnose'
                    ? 'border-blue-600 text-blue-600 dark:text-sky-400 dark:border-sky-400 bg-white dark:bg-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                }`}
              >
                <ShieldAlert className="w-3.5 h-3.5" />
                <span>Diagnostics</span>
                <span className="text-[9px] bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 px-1 rounded font-mono font-bold">Admin</span>
              </button>
            </>
          )}
        </div>

        {/* ── Tab 1: Chat Assistant ─────────────────────────────────────────── */}
        {activeTab === 'chat' && (
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`}
              >
                {/* Agent Header / Routing Badge */}
                {msg.sender === 'agent' && msg.agentType && (
                  <div className="flex items-center gap-1.5 mb-1 text-[11px] font-mono">
                    <span
                      className={`px-2 py-0.5 rounded-full border ${
                        msg.agentType.includes('Reasoning')
                          ? 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800'
                          : 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800'
                      }`}
                    >
                      {msg.agentType}
                    </span>
                    <span className="text-slate-400 text-[10px]">{msg.timestamp}</span>
                  </div>
                )}

                {/* Message Bubble */}
                <div
                  className={`max-w-[90%] p-3.5 rounded-2xl text-xs leading-relaxed shadow-sm ${
                    msg.sender === 'user'
                      ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-br-none'
                      : 'bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-bl-none'
                  }`}
                >
                  <div className="whitespace-pre-wrap">{msg.text}</div>

                  {/* Cited Sources Badge List */}
                  {msg.sources && msg.sources.length > 0 && (
                    <div className="mt-3 pt-2.5 border-t border-slate-200 dark:border-slate-700 text-[11px]">
                      <div className="flex items-center gap-1 text-slate-500 dark:text-slate-400 font-semibold mb-1">
                        <BookOpen className="w-3 h-3 text-sky-500" />
                        <span>Cited Documentation:</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {msg.sources.map((src, sIdx) => (
                          <span
                            key={sIdx}
                            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-2 py-0.5 rounded text-[10px] text-slate-600 dark:text-slate-300 font-mono"
                          >
                            {src.document} {src.section ? `> ${src.section}` : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actionable Remediation Box */}
                  {msg.remediation && (
                    <div className="mt-3 p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-emerald-900 dark:text-emerald-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-[11px] flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                          Recommended Fix:
                        </span>
                        <button
                          onClick={() => handleCopyText(msg.id, msg.remediation)}
                          className="text-[10px] text-emerald-700 dark:text-emerald-400 hover:underline flex items-center gap-0.5"
                        >
                          {copiedId === msg.id ? (
                            <>
                              <Check className="w-3 h-3" /> Copied
                            </>
                          ) : (
                            <>
                              <Copy className="w-3 h-3" /> Copy
                            </>
                          )}
                        </button>
                      </div>
                      <p className="text-[11px] font-mono">{msg.remediation}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex items-center gap-2 p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 max-w-[70%]">
                <RefreshCw className="w-3.5 h-3.5 text-sky-500 animate-spin" />
                <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">
                  SRE Brain reasoning...
                </span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* ── Tab 2: Live Telemetry (Super Admin Only) ───────────────────────── */}
        {activeTab === 'telemetry' && isSuperAdmin && (
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="p-3.5 rounded-xl bg-gradient-to-br from-slate-900 to-slate-800 text-white border border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Server className="w-4 h-4 text-sky-400" />
                  <span className="font-semibold text-xs">SRE Orchestrator Service</span>
                </div>
                <button
                  onClick={() => {
                    fetchHealth();
                    fetchTelemetry();
                  }}
                  className="p-1 rounded hover:bg-slate-700 text-slate-300"
                  title="Refresh health and telemetry"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isHealthLoading || isTelemetryLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                <div className="p-2 rounded bg-slate-800/80 border border-slate-700">
                  <span className="text-slate-400 text-[10px] block">Service Status</span>
                  <span className={`font-semibold ${healthStatus?.status === 'UP' || healthStatus?.status === 'HEALTHY' ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {healthStatus?.status || 'CHECKING...'}
                  </span>
                </div>
                <div className="p-2 rounded bg-slate-800/80 border border-slate-700">
                  <span className="text-slate-400 text-[10px] block">RAG Indexed Records</span>
                  <span className="text-sky-300 font-semibold">{healthStatus?.vector_store_records || '177'} chunks</span>
                </div>
                <div className="p-2 rounded bg-slate-800/80 border border-slate-700">
                  <span className="text-slate-400 text-[10px] block">LLM Engine</span>
                  <span className="text-amber-300 font-semibold">{healthStatus?.primary_llm || 'Gemini 2.0 Flash'}</span>
                </div>
                <div className="p-2 rounded bg-slate-800/80 border border-slate-700">
                  <span className="text-slate-400 text-[10px] block">Loaded Runbooks</span>
                  <span className="text-purple-300 font-semibold">{healthStatus?.loaded_runbooks_count || '3'} active</span>
                </div>
              </div>
            </div>

            {/* Live Runtime Metrics (HikariCP & JVM Memory) */}
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center justify-between">
                <span>Active Runtime Telemetry</span>
                <span className="text-[10px] text-emerald-500 font-mono flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span> Live
                </span>
              </h4>

              <div className="grid grid-cols-2 gap-2 text-xs">
                {/* HikariCP Connection Pool Gauge */}
                <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-slate-700 dark:text-slate-300">HikariCP Pool</span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 font-bold">
                      {telemetryData?.telemetry?.hikaricp?.pool_status || 'OPTIMAL'}
                    </span>
                  </div>
                  <div className="text-[11px] font-mono space-y-0.5 text-slate-600 dark:text-slate-400">
                    <div>Active: <span className="text-blue-600 dark:text-blue-400 font-bold">{telemetryData?.telemetry?.hikaricp?.active_connections ?? 2}</span></div>
                    <div>Idle: <span className="font-bold">{telemetryData?.telemetry?.hikaricp?.idle_connections ?? 8}</span> / Max: {telemetryData?.telemetry?.hikaricp?.max_connections ?? 10}</div>
                  </div>
                </div>

                {/* JVM Memory */}
                <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-slate-700 dark:text-slate-300">JVM Heap</span>
                    <span className="text-[10px] font-mono text-slate-500">
                      {telemetryData?.telemetry?.jvm?.threads_live ?? 42} threads
                    </span>
                  </div>
                  <div className="text-[11px] font-mono space-y-0.5 text-slate-600 dark:text-slate-400">
                    <div>Used: <span className="text-purple-600 dark:text-purple-400 font-bold">{telemetryData?.telemetry?.jvm?.heap_used_mb ?? 248} MB</span></div>
                    <div>Max: {telemetryData?.telemetry?.jvm?.heap_max_mb ?? 1024} MB</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Platform Subsystem Grid */}
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Connected Infrastructure</h4>
              <div className="p-2.5 rounded-lg border border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <Database className="w-4 h-4 text-emerald-500" />
                  <div>
                    <span className="font-medium text-slate-800 dark:text-slate-200">PostgreSQL 16 (Server B)</span>
                    <span className="text-[10px] text-slate-400 block">Schemas: yaseero_master, yaseero_tenant</span>
                  </div>
                </div>
                <span className="text-[10px] bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 px-2 py-0.5 rounded font-mono">
                  sre_readonly
                </span>
              </div>

              <div className="p-2.5 rounded-lg border border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-sky-500" />
                  <div>
                    <span className="font-medium text-slate-800 dark:text-slate-200">Grafana Loki (Server A)</span>
                    <span className="text-[10px] text-slate-400 block">LogQL: backend, frontend, keycloak</span>
                  </div>
                </div>
                <span className="text-[10px] bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300 border border-sky-200 px-2 py-0.5 rounded font-mono">
                  Port 3100
                </span>
              </div>

              <div className="p-2.5 rounded-lg border border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <Cpu className="w-4 h-4 text-indigo-500" />
                  <div>
                    <span className="font-medium text-slate-800 dark:text-slate-200">Spring Boot Actuator</span>
                    <span className="text-[10px] text-slate-400 block">Health, HikariCP, Metrics</span>
                  </div>
                </div>
                <span className="text-[10px] bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 border border-indigo-200 px-2 py-0.5 rounded font-mono">
                  Port 8081
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ── Tab 3: Diagnostics Simulator (Super Admin Only) ──────────────── */}
        {activeTab === 'diagnose' && isSuperAdmin && (
          <div className="flex-1 overflow-y-auto p-4 space-y-4 text-xs">
            <div>
              <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-1">Simulate Production Incidents</h4>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-3">
                Select a simulated incident to observe the HolmesGPT Level 2 diagnostic engine runbooks in real time:
              </p>
              <div className="space-y-2">
                {SIMULATED_ERRORS.map((err, eIdx) => (
                  <button
                    key={eIdx}
                    onClick={() =>
                      handleSend(err.title, {
                        stackTrace: err.trace,
                        statusCode: 500
                      })
                    }
                    className="w-full text-left p-2.5 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-blue-500 dark:hover:border-sky-500 bg-slate-50 dark:bg-slate-800/60 hover:bg-white dark:hover:bg-slate-800 transition-all flex items-center justify-between group"
                  >
                    <div>
                      <span className="font-semibold text-slate-800 dark:text-slate-200 block">{err.title}</span>
                      <span className="text-[10px] text-slate-500 font-mono line-clamp-1">{err.trace}</span>
                    </div>
                    <Terminal className="w-4 h-4 text-slate-400 group-hover:text-blue-500 transition-colors" />
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-3 border-t border-slate-200 dark:border-slate-800">
              <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-1">Paste Live Stack Trace</h4>
              <textarea
                value={customTrace}
                onChange={(e) => setCustomTrace(e.target.value)}
                placeholder="Paste Java exception trace or browser console error here..."
                rows={4}
                className="w-full p-2.5 text-xs font-mono rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={() => {
                  if (customTrace.trim()) {
                    handleSend('Live Exception Diagnostics', {
                      stackTrace: customTrace,
                      statusCode: 500
                    });
                    setCustomTrace('');
                  }
                }}
                disabled={!customTrace.trim() || isLoading}
                className="mt-2 w-full py-2 px-3 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium text-xs flex items-center justify-center gap-2 shadow-md transition-all disabled:opacity-50"
              >
                <ShieldAlert className="w-3.5 h-3.5" />
                <span>Run Autonomous Diagnosis</span>
              </button>
            </div>
          </div>
        )}

        {/* ── Suggested Prompt Chips (in Chat tab) ─────────────────────────── */}
        {activeTab === 'chat' && (
          <div className="p-2 border-t border-slate-100 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-900/30">
            <div className="flex gap-1.5 overflow-x-auto pb-1 no-scrollbar text-[11px]">
              {SUGGESTED_PROMPTS.map((prompt, pIdx) => (
                <button
                  key={pIdx}
                  onClick={() => handleSend(prompt)}
                  className="whitespace-nowrap px-2.5 py-1 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-sky-400 dark:hover:border-sky-500 hover:text-sky-600 transition-colors"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Input Bar ─────────────────────────────────────────────────────── */}
        <div className="p-3 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
            className="flex items-center gap-2"
          >
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Ask anything or describe an error..."
              disabled={isLoading}
              className="flex-1 px-3 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
            />
            <button
              type="submit"
              disabled={!inputText.trim() || isLoading}
              className="p-2 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Send query"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default AskAiDrawer;
